import {
  addDoc,
  collection,
  doc,
  DocumentData,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import { downloadImage, fetchDocument, uploadImageToFirebase } from './utils'
import { addCoordinatesToLocation } from 'hook/listings/useFetchListings'

const listingRef = collection(firebaseDb, 'listings')
const ingredientsRef = collection(firebaseDb, 'ingredients')
const marketingRef = collection(firebaseDb, 'marketingEmail')

export async function UploadListingData(listingData: any, listingId?: string) {
  const { photo } = listingData || {}

  try {
    if (photo) {
      const imageURL = await uploadImageToFirebase('listings', photo)

      listingData.photo = imageURL
      listingData.vendors.photo = imageURL
    }

    if (listingId) {
      await setDoc(
        doc(collection(firebaseDb, 'listings'), listingId),
        listingData
      )
    } else {
      await addDoc(listingRef, listingData)
    }

    return true
  } catch (error) {
    console.error('Error saving listing:', error)
  }
}

export async function UploadMarketingData(
  email: string,
  firstName?: string,
  lastName?: string,
  id?: string
) {
  try {
    const data: any = { email }
    if (firstName) data.firstName = firstName
    if (lastName) data.lastName = lastName
    data.createdAt = new Date().toISOString()

    await addDoc(marketingRef, data)

    return true
  } catch (error) {
    console.error('Error in listings operation:', error)
    throw error
  }
}

export async function UploadIngredientsData(
  ingredientData: any,
  listingId?: string
) {
  const { photo } = ingredientData || {}

  try {
    const imageURL = await uploadImageToFirebase('listings', photo)

    ingredientData.photo = imageURL

    if (listingId) {
      await setDoc(
        doc(collection(firebaseDb, 'ingredients'), listingId),
        ingredientData
      )
    } else {
      await addDoc(ingredientsRef, ingredientData)
    }

    return true
  } catch (error) {
    console.error('Error deleting listing:', error)
  }
}

function convertToKeyValuePairs(csvData: any) {
  const headers = csvData[0]
  const data = csvData.slice(1)

  const result = data.reduce((acc: any, item: any) => {
    const obj = {}

    headers.forEach((header: any, i: any) => {
      const value = item[i].trim()
      //@ts-ignore
      obj[header] = value
    })

    acc.push(obj)

    return acc
  }, [])

  return result
}

function extractSocialLinks(socialString: any) {
  const links = socialString?.split(',').map((link: any) => link.trim())
  const social = {
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: '',
  }

  links.forEach((link: any) => {
    if (link.includes('facebook.com')) {
      social.facebook = link
    } else if (link.includes('instagram.com')) {
      social.instagram = link
    } else if (link.includes('twitter.com')) {
      social.twitter = link
    } else if (link.includes('youtube.com')) {
      social.youtube = link
    }
  })

  return social
}

export async function uploadIngredientsCSVData(parsedData: any) {
  const convertedData = convertToKeyValuePairs(parsedData)

  const promises = convertedData.map(async (row: any) => {
    const listingData = {
      metadata: {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      photo: row['Photo'],
      name: row['Name'],
      description: row['Description'],
      type: row['Type'],
      isSeasonal: false,
    }

    const imageBuffer = await downloadImage(row['Photo'])
    const imageURL = await uploadImageToFirebase('listings', imageBuffer, true)
    listingData.photo = imageURL

    try {
      await addDoc(ingredientsRef, listingData)
      return null // Return null or some success indicator
    } catch (error) {
      console.error('Error importing ingredients:', error)
      return `Error importing ingredients`
    }
  })

  const results = await Promise.all(promises)

  if (results.some((result) => result !== null)) {
    return `Some listings were not imported successfully`
  } else {
    return `Successfully imported all listings`
  }
}

export async function uploadCSVData(parsedData: any) {
  async function fetchIngredientData(ingredientId: string) {
    try {
      return await fetchDocument('ingredients', ingredientId)
    } catch (error) {
      console.error('Error fetching ingredient data:', error)
      return null
    }
  }

  function processSocialLinks(socialLinks: string) {
    return socialLinks
      .split(',')
      .map((link: string) => link.trim())
      .filter(
        (link: string) =>
          !link.includes('facebook.com') &&
          !link.includes('instagram.com') &&
          !link.includes('twitter.com')
      )
  }

  const convertedData = convertToKeyValuePairs(parsedData)

  const promises = convertedData.map(async (row: any) => {
    // const ingredientIds = row['Ingredients']
    //   .split(',')
    //   .map((id: string) => id.trim())
    // const ingredientsData =
    //   ingredientIds?.length &&
    //   (await Promise.all(ingredientIds.map(fetchIngredientData)))

    const listingData = {
      contact_info: {
        first_name: row['First Name'] || '',
        last_name: row['Last Name'] || '',
        email: row['Email'] || '',
        phone: row['Phone'] || '',
      },
      metadata: {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        public: true,
        type: row['Type'] || 'Basic',
      },
      photo: row['Photo'] || null,
      name: row['Farm Name'] || '',
      description: row['Description'] || '',
      type: row['Listing Type'].toLowerCase() || '',
      location: {
        address: row['Street'] || '',
        city: row['City'] || '',
        state: row['State'] || '',
        zip: row['Zip'] || '',
      },
      hours: {
        start: row['Opening Hours Start'] || '',
        end: row['Opening Hours End'] || '',
      },
      // days: row['Days']?.split(',').map((day: string) => day.trim()),
      website: row['Website'] || '',
      // social: processSocialLinks(row['Social']),
      // custom_links: processSocialLinks(row['Social']),
      public_email: row['Public Email'] || '',
      public_phone: row['Public Phone'] || '',
      // ingredients: ingredientsData.filter((data: any) => data !== null),
      vendors: [],
    }


    const newLocation = await addCoordinatesToLocation(listingData?.location)

    listingData.location = newLocation

    const imageBuffer = row['Photo'] && (await downloadImage(row['Photo']))
    const imageURL =
      imageBuffer &&
      (await uploadImageToFirebase('listings', imageBuffer, true))

    if (imageURL) {
      listingData.photo = imageURL
    }

    try {
      await addDoc(listingRef, listingData)
      return null // Return null or some success indicator
    } catch (error) {
      console.error('Error importing listing:', error)
      return `Error importing listing`
    }
  })

  const results = await Promise.all(promises)

  if (results.some((result) => result !== null)) {
    return `Some listings were not imported successfully`
  } else {
    return `Successfully imported all listings`
  }
}

export async function fetchFilteredListings(
  searchQuery: any
): Promise<DocumentData[]> {
  try {
    const { type, location, ingredient } = searchQuery

    let baseQuery = query(collection(firebaseDb, 'listings'))

    if (type) {
      baseQuery = query(baseQuery, where('type', '==', type.toLowerCase()))
    }

    let locationQuery: any = baseQuery

    if (location) {
      const locationLowerCase = location.toLowerCase()
      locationQuery = query(
        locationQuery,
        where('location.city', '==', locationLowerCase),
        where('location.state', '==', locationLowerCase)
      )
    }

    if (ingredient) {
      baseQuery = query(
        baseQuery,
        where('ingredients', 'array-contains', {
          name: ingredient.toLowerCase(),
        })
      )
    }

    const docSnap = await getDocs(baseQuery)

    return docSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as DocumentData[]
  } catch (error) {
    console.error('Error fetching listings:', error)
    return []
  }
}
